import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { api } from '../utils/api';

const STEPS = [
  {
    target: 'profile-link-card',
    title: 'Your shareable link',
    body: "This is your LinkMonkey URL. Copy it and drop it into your YouTube, Instagram or TikTok bio so fans find everything you do in one place.",
  },
  {
    target: 'stats-grid',
    title: 'Live stats',
    body: 'Profile views, link clicks and active links at a glance — the numbers update as fans visit your page.',
  },
  {
    target: 'your-links-card',
    title: 'Your links',
    body: 'Everything on your public profile lives here. Add your first link, then reorder them anytime with drag & drop in Settings.',
  },
  {
    target: 'quick-post-card',
    title: 'Quick post to your Fan Page',
    body: 'Share a thought, video or song straight to your Bebo-style Fan Page without leaving the dashboard.',
  },
  {
    target: 'visitor-map-card',
    title: 'Visitor map',
    body: 'Once your profile gets traffic, see which countries your visitors come from.',
  },
  {
    target: 'edit-profile-btn',
    title: 'Make it yours',
    body: 'Head to Edit Profile for themes, wallpapers, fonts, Tip Jar, timed links, your QR code and more.',
  },
];

const PAD = 10;

export default function DashboardTour({ active, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const measureTimer = useRef(null);

  const measure = useCallback((idx) => {
    const el = document.querySelector(`[data-testid="${STEPS[idx].target}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: 'center', behavior: 'auto' });
    clearTimeout(measureTimer.current);
    measureTimer.current = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 120);
  }, []);

  useEffect(() => {
    if (!active) return;
    setStep(0);
    measure(0);
  }, [active, measure]);

  useEffect(() => {
    if (!active) return;
    const onResize = () => measure(step);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); clearTimeout(measureTimer.current); };
  }, [active, step, measure]);

  if (!active) return null;

  const goTo = (idx) => {
    if (idx < 0) return;
    if (idx >= STEPS.length) { finish(); return; }
    setStep(idx);
    measure(idx);
  };

  const finish = () => {
    api.completeTour().catch(() => {});
    onClose();
  };

  const isLast = step === STEPS.length - 1;
  const s = STEPS[step];

  // Tooltip placement: below the spotlight, flip above if too low.
  let tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
  if (rect) {
    const below = rect.top + rect.height + PAD + 16;
    const flipUp = below + 210 > window.innerHeight;
    const left = Math.min(Math.max(rect.left, 16), window.innerWidth - 356);
    tipStyle = flipUp
      ? { bottom: window.innerHeight - rect.top + PAD + 12, left }
      : { top: below, left };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="dashboard-tour">
      {/* Spotlight hole */}
      {rect ? (
        <motion.div
          className="absolute rounded-2xl pointer-events-none"
          initial={false}
          animate={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          style={{
            boxShadow: '0 0 0 9999px rgba(2,10,30,0.62), 0 0 0 3px rgba(0,85,255,0.65), 0 0 40px rgba(0,85,255,0.35)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(2,10,30,0.62)]" />
      )}

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 14, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="absolute w-[340px] max-w-[calc(100vw-32px)] rounded-2xl bg-white border border-gray-200 shadow-2xl p-5"
          style={tipStyle}
          data-testid="tour-tooltip"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#0055FF] text-white flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5" />
              </span>
              <h4 className="font-display text-base font-bold tracking-tight text-gray-950">{s.title}</h4>
            </div>
            <button onClick={finish} className="text-gray-400 hover:text-gray-700 transition-colors" aria-label="Skip tour" data-testid="tour-skip-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed mb-4 font-body">{s.body}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5" data-testid="tour-progress">
              {STEPS.map((_, i) => (
                <span key={i} className={`rounded-full transition-all duration-300 ${i === step ? 'w-5 h-1.5 bg-[#0055FF]' : 'w-1.5 h-1.5 bg-gray-300'}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={() => goTo(step - 1)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-[#C9DEF9] transition-colors" data-testid="tour-back-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button onClick={() => goTo(step + 1)} className="inline-flex items-center gap-1 text-xs font-semibold px-4 py-2 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors shadow-[0_6px_20px_rgba(0,85,255,0.4)]" data-testid="tour-next-btn">
                {isLast ? 'Finish' : 'Next'} {!isLast && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
